/**
 * Auth schemas — login, register, refresh, password reset.
 */

import { z } from 'zod';
import { emailSchema, idSchema, iso8601Schema } from './common';

export const loginInputSchema = z.object({
  email: emailSchema,
  password: z.string().min(8).max(200),
});
export type LoginInput = z.infer<typeof loginInputSchema>;

const loginUserSchema = z.object({
  id: idSchema,
  email: emailSchema,
  displayName: z.string(),
  role: z.string(),
});

export const loginSuccessSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
  mfaRequired: z.literal(false).optional(),
  user: loginUserSchema,
});

export const loginChallengeSchema = z.object({
  accessToken: z.literal(''),
  refreshToken: z.literal(''),
  expiresIn: z.literal(0),
  mfaRequired: z.literal(true),
  mfaToken: z.string().min(1),
  user: loginUserSchema,
});

export const loginOutputSchema = z.union([loginChallengeSchema, loginSuccessSchema]);
export type LoginOutput = z.infer<typeof loginOutputSchema>;

export const refreshInputSchema = z.object({
  refreshToken: z.string().min(10),
});

export const logoutInputSchema = z.object({
  refreshToken: z.string().min(10).optional(),
});

export const forgotPasswordInputSchema = z.object({
  email: emailSchema,
});

export const resetPasswordInputSchema = z.object({
  token: z.string().min(20),
  newPassword: z.string().min(12).max(200),
});

export const changePasswordInputSchema = z.object({
  currentPassword: z.string().min(8),
  newPassword: z.string().min(12).max(200),
});

export const sessionOutputSchema = z.object({
  user: z.object({
    id: idSchema,
    email: emailSchema,
    displayName: z.string(),
    role: z.string(),
    createdAt: iso8601Schema,
    /** Daily morning digest (10:00 PKT) opt-out flag — default false = ON. */
    digestOptOut: z.boolean(),
  }),
});
export type SessionOutput = z.infer<typeof sessionOutputSchema>;

/** PATCH /v1/auth/preferences — own-user notification preferences. */
export const updatePreferencesInputSchema = z.object({
  digestOptOut: z.boolean(),
});
export type UpdatePreferencesInput = z.infer<typeof updatePreferencesInputSchema>;